import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FileText, FileX2, Clock3, CheckCircle2, Search, Plus, RotateCcw,
  ChevronLeft, ChevronRight, FileSignature,
} from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { EXPIRY_FILTERS, expiryFilterLabel } from "@/lib/docStatus";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DocumentsTable from "@/components/documents/DocumentsTable";
import UploadDocumentModal from "@/components/documents/UploadDocumentModal";
import PlayerSheet from "@/components/PlayerSheet";
import { useLang, translateFr as tfr } from "@/i18n";

const KPI_DEFS = [
  { key: "total", status: "all", labelKey: "docs.kpi.total", icon: FileText, tone: "info" },
  { key: "expired", status: "expired", labelKey: "docs.kpi.expired", icon: FileX2, tone: "danger" },
  { key: "expiring_soon", status: "expiring", labelKey: "docs.kpi.expiring", icon: Clock3, tone: "warning" },
  { key: "up_to_date", status: "valid", labelKey: tfr("docs.kpi.valid"), icon: CheckCircle2, tone: "success" },
];

const TONES = {
  info: { text: "text-info", tile: "bg-info/10 border-info/25 text-info", active: "border-info/60 ring-1 ring-info/40" },
  danger: { text: "text-danger", tile: "bg-danger/10 border-danger/25 text-danger", active: "border-danger/60 ring-1 ring-danger/40" },
  warning: { text: "text-warning", tile: "bg-warning/10 border-warning/25 text-warning", active: "border-warning/60 ring-1 ring-warning/40" },
  success: { text: "text-success", tile: "bg-success/10 border-success/25 text-success", active: "border-success/60 ring-1 ring-success/40" },
};

const LIMITS = [10, 25, 50];

function DocKpi({ def, value, active, onClick }) {
  const { t: tr } = useLang();
  const t = TONES[def.tone];
  const Icon = def.icon;
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      data-testid={`documents-kpi-${def.key}`}
      className={cn(
        "group text-left rounded-2xl border bg-card p-4 md:p-5 transition-all duration-200",
        "hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        active ? t.active : "border-border hover:border-foreground/20"
      )}
    >
      <div className="flex items-center gap-2.5">
        <div className={cn("h-8 w-8 rounded-lg border flex items-center justify-center shrink-0", t.tile)}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-[10px] md:text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground truncate">
          {tr(def.labelKey)}
        </span>
      </div>
      <div className={cn("mt-3 text-3xl md:text-4xl leading-none font-semibold tabular-nums tracking-tight", t.text)} data-testid={`documents-kpi-${def.key}-value`}>
        {value ?? "—"}
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">
        {active ? tr("docs.activeFilter") : tr("docs.clickToFilter")}
      </div>
    </button>
  );
}

function Pagination({ pagination, limit, onPage, onLimit }) {
  const { page, total, total_pages } = pagination;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const pages = [];
  const start = Math.max(1, Math.min(page - 2, total_pages - 4));
  for (let i = start; i <= Math.min(total_pages, start + 4); i++) pages.push(i);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="documents-pagination">
      <div className="text-xs text-muted-foreground tabular-nums" data-testid="documents-pagination-info">
        {total === 0 ? "0 document" : `${from}–${to} sur ${total} document${total > 1 ? "s" : ""}`}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden md:inline">Lignes par page</span>
          <Select value={String(limit)} onValueChange={(v) => onLimit(parseInt(v, 10))}>
            <SelectTrigger className="h-8 w-[70px] text-xs" data-testid="documents-rows-per-page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIMITS.map((l) => (
                <SelectItem key={l} value={String(l)} data-testid={`documents-rows-option-${l}`}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/60 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            aria-label={tfr("Page précédente")}
            data-testid="documents-page-prev"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          {pages.map((p) => (
            <button
              key={p}
              onClick={() => onPage(p)}
              className={cn(
                "h-8 min-w-8 px-2 rounded-lg border text-xs font-medium tabular-nums transition-colors",
                p === page
                  ? "border-foreground/30 bg-foreground/10 text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              )}
              data-testid={`documents-page-${p}`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => onPage(page + 1)}
            disabled={page >= total_pages}
            className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/60 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            aria-label={tfr("Page suivante")}
            data-testid="documents-page-next"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="documents-skeleton">
      <div className="divide-y divide-border">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-4 animate-pulse">
            <div className="h-9 w-9 rounded-lg bg-secondary" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-1/3 rounded bg-secondary" />
              <div className="h-2.5 w-1/4 rounded bg-secondary/70" />
            </div>
            <div className="h-5 w-16 rounded-full bg-secondary" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Documents() {
  const { t, tf } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "all";
  const q = searchParams.get("q") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const limitRaw = parseInt(searchParams.get("limit") || "10", 10);
  const limit = LIMITS.includes(limitRaw) ? limitRaw : 10;
  const sort = searchParams.get("sort") || "expiration";
  const order = searchParams.get("order") || "asc";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState(q);
  const [openPlayer, setOpenPlayer] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  const updateParams = useCallback((patch, { resetPage = false } = {}) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === "" || (k === "status" && v === "all")) p.delete(k);
        else p.set(k, String(v));
      });
      if (resetPage) p.delete("page");
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/documents", {
        params: { status, q: q || undefined, page, limit, sort, order },
      });
      setData(r.data);
    } catch {
      setData({ items: [], pagination: { page: 1, limit, total: 0, total_pages: 1 }, stats: null, error: true });
    } finally {
      setLoading(false);
    }
  }, [status, q, page, limit, sort, order]);

  useEffect(() => { load(); }, [load]);

  // Debounced server-side search
  useEffect(() => {
    const t = setTimeout(() => {
      if (qInput.trim() !== q) updateParams({ q: qInput.trim() }, { resetPage: true });
    }, 350);
    return () => clearTimeout(t);
  }, [qInput, q, updateParams]);

  const setStatus = (v) => updateParams({ status: v }, { resetPage: true });

  const toggleSort = (field) => {
    if (sort === field) updateParams({ order: order === "asc" ? "desc" : "asc" });
    else updateParams({ sort: field, order: "asc" });
  };

  const resetFilters = () => {
    setQInput("");
    updateParams({ status: null, q: null, sort: null, order: null }, { resetPage: true });
  };

  const handleAddClick = () => {
    if (billing && (billing.state === "expired" || billing.state === "past_due")) {
      window.dispatchEvent(new CustomEvent("athlynx:paywall", {
        detail: { message: tfr("Votre essai est expiré — abonnez-vous pour ajouter des documents.") },
      }));
      return;
    }
    setUploadOpen(true);
  };

  const stats = data?.stats;
  const items = data?.items || [];
  const pagination = data?.pagination || { page: 1, limit, total: 0, total_pages: 1 };
  const hasActiveFilters = status !== "all" || !!q;

  return (
    <div className="animate-fade-in p-4 md:p-6 space-y-5 max-w-[1280px] mx-auto" data-testid="documents-page">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{t("docs.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("docs.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <Button onClick={handleAddClick} className="gap-1.5 shrink-0" data-testid="documents-add-btn">
            <Plus className="h-4 w-4" /> {t("docs.add")}
          </Button>
        </div>
      </header>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="documents-kpi-grid">
        {KPI_DEFS.map((def) => (
          <DocKpi
            key={def.key}
            def={def}
            value={stats ? stats[def.key] : null}
            active={status === def.status}
            onClick={() => setStatus(def.status)}
          />
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3" data-testid="documents-toolbar">
        <div className="relative w-full lg:w-72 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={t("docs.search")}
            className="h-9 pl-9 text-sm border-border bg-card"
            data-testid="documents-search"
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide" data-testid="documents-filters">
          {EXPIRY_FILTERS.map((f) => {
            const active = status === f.v;
            return (
              <button
                key={f.v}
                onClick={() => setStatus(f.v)}
                aria-pressed={active}
                className={cn(
                  "shrink-0 inline-flex items-center h-8 px-3.5 rounded-full text-xs font-medium border transition-colors",
                  active
                    ? "bg-foreground/10 text-foreground border-foreground/25"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                )}
                data-testid={`documents-filter-${f.v}`}
              >
                {expiryFilterLabel(f)}
              </button>
            );
          })}
        </div>
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground transition-colors lg:ml-auto"
            data-testid="documents-reset-filters"
          >
            <RotateCcw className="h-3 w-3" /> Réinitialiser
          </button>
        )}
      </div>

      {/* Content */}
      {loading && !data ? (
        <TableSkeleton />
      ) : items.length === 0 ? (
        <div className="space-y-4">
        <div className="rounded-2xl border border-dashed border-border bg-card py-16 text-center" data-testid="documents-empty">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-secondary/80 border border-border flex items-center justify-center mb-4">
            <FileText className="h-5 w-5 text-muted-foreground/60" />
          </div>
          {stats && stats.total === 0 ? (
            <>
              <div className="text-sm font-medium">{tfr("Aucun document pour l'instant")}</div>
              <div className="text-xs text-muted-foreground mt-1 mb-4">
                {tfr("Ajoutez le premier document de votre portefeuille (contrat, CV, pièce d'identité…).")}
              </div>
              <Button size="sm" onClick={handleAddClick} className="gap-1.5" data-testid="documents-empty-add-btn">
                <Plus className="h-3.5 w-3.5" /> Ajouter un document
              </Button>
            </>
          ) : (
            <>
              <div className="text-sm font-medium">{tfr("Aucun résultat")}</div>
              <div className="text-xs text-muted-foreground mt-1 mb-4">
                {tfr("Aucun document ne correspond à ces filtres.")}
              </div>
              <Button size="sm" variant="outline" onClick={resetFilters} className="gap-1.5" data-testid="documents-empty-reset-btn">
                <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser les filtres
              </Button>
            </>
          )}
        </div>
        {stats && stats.total > 0 && (
          <Pagination
            pagination={pagination}
            limit={limit}
            onPage={(p) => updateParams({ page: p })}
            onLimit={(l) => updateParams({ limit: l }, { resetPage: true })}
          />
        )}
        </div>
      ) : (
        <div className={cn("space-y-4 transition-opacity", loading && "opacity-60 pointer-events-none")}>
          <DocumentsTable
            items={items}
            sort={sort}
            order={order}
            onSort={toggleSort}
            onPlayerClick={(pid) => pid && setOpenPlayer(pid)}
            onChanged={load}
          />
          <Pagination
            pagination={pagination}
            limit={limit}
            onPage={(p) => updateParams({ page: p })}
            onLimit={(l) => updateParams({ limit: l }, { resetPage: true })}
          />
        </div>
      )}

      <PlayerSheet
        playerId={openPlayer}
        open={!!openPlayer}
        onOpenChange={(o) => !o && setOpenPlayer(null)}
        onUpdated={load}
      />
      <UploadDocumentModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={load}
      />
    </div>
  );
}
